import type { CoverageLookupHit } from "./coverageLookup.js";

export const NOTE_REVIEW_DAYS = 30;

export type CoverageNote = {
  id: string;
  partyId: string;
  body: string;
  program: string;
  sizeLabel: string;
  reviewAfter: string;
  invalidatedAt: string | null;
};

export type IssueCounts = {
  delivery: number;
  installation: number;
  customer: number;
};

export const EMPTY_ISSUE_COUNTS: IssueCounts = { delivery: 0, installation: 0, customer: 0 };

export type SuggestionShortlistRow = {
  partyId: string;
  name: string;
  tier: CoverageLookupHit["tier"];
  milesFromPlant: number | null;
  notes: string[];
  issues: IssueCounts;
};

export type CoverageSuggestion = {
  partyId: string;
  reason: string;
};

export function noteIsValid(note: CoverageNote, nowMs: number, program: string, sizeLabel: string): boolean {
  if (note.invalidatedAt) return false;
  const reviewAt = Date.parse(note.reviewAfter);
  if (!Number.isFinite(reviewAt) || reviewAt <= nowMs) return false;
  const noteProgram = note.program.trim().toLowerCase();
  const wantProgram = program.trim().toLowerCase();
  if (noteProgram && noteProgram !== wantProgram) return false;
  const noteSize = note.sizeLabel.trim().toLowerCase();
  const wantSize = sizeLabel.trim().toLowerCase();
  if (noteSize && noteSize !== wantSize) return false;
  return true;
}

export function shortlistForSuggestion(
  hits: CoverageLookupHit[],
  notes: CoverageNote[],
  nowMs: number,
  program: string,
  sizeLabel: string,
): SuggestionShortlistRow[] {
  const ordered = [...hits].sort((a, b) => {
    if (a.tier !== b.tier) return a.tier === "preferred" ? -1 : 1;
    if (a.milesFromPlant == null && b.milesFromPlant == null) return a.name.localeCompare(b.name);
    if (a.milesFromPlant == null) return 1;
    if (b.milesFromPlant == null) return -1;
    return a.milesFromPlant - b.milesFromPlant;
  });
  return ordered.map((hit) => ({
    partyId: hit.partyId,
    name: hit.name,
    tier: hit.tier,
    milesFromPlant: hit.milesFromPlant,
    notes: notes
      .filter((note) => note.partyId === hit.partyId && noteIsValid(note, nowMs, program, sizeLabel))
      .map((note) => note.body),
    issues: EMPTY_ISSUE_COUNTS,
  }));
}

export function suggestFromFacts(rows: SuggestionShortlistRow[]): CoverageSuggestion | null {
  const winner = rows[0];
  if (!winner) return null;
  const where = winner.tier === "preferred" ? "the preferred ring" : "the full territory";
  const miles =
    winner.milesFromPlant == null ? "distance from the plant is unknown" : `${winner.milesFromPlant} miles from the plant`;
  const note = winner.notes[0]?.trim().replace(/[.!?]+$/, "");
  const quoted = note ? `, and the valid note says: ${note}` : "";
  const issues = issueClause(winner.issues);
  return {
    partyId: winner.partyId,
    reason: `${winner.name}, because it sits in ${where} and is ${miles}${quoted}${issues}.`,
  };
}

function issueClause(counts: IssueCounts | undefined): string {
  const parts = [
    counts?.delivery ? `${counts.delivery} delivery` : "",
    counts?.installation ? `${counts.installation} installation` : "",
    counts?.customer ? `${counts.customer} customer` : "",
  ].filter(Boolean);
  return parts.length ? `, and recent issues are ${parts.join(", ")}` : "";
}

export function reasonUsesNote(reason: string, notes: string[]): boolean {
  const text = reason.toLowerCase();
  return notes.some((note) => {
    const sample = note.trim().toLowerCase();
    if (!sample) return false;
    return text.includes(sample.slice(0, Math.min(sample.length, 24)));
  });
}

export function suggestionWithNotes(
  pick: CoverageSuggestion | null,
  rows: SuggestionShortlistRow[],
): CoverageSuggestion | null {
  if (!pick) return suggestFromFacts(rows);
  const row = rows.find((item) => item.partyId === pick.partyId);
  if (!row || reasonCoversFacts(pick.reason, row)) return pick;
  return suggestFromFacts([row]);
}

function reasonCoversFacts(reason: string, row: SuggestionShortlistRow): boolean {
  const text = reason.toLowerCase();
  const hasPlace = text.includes("preferred") || text.includes("full territory") || text.includes("full ring");
  const hasMiles = row.milesFromPlant == null || text.includes(String(row.milesFromPlant));
  const hasNote = row.notes.length === 0 || reasonUsesNote(reason, row.notes);
  const hasIssues = !hasIssueCounts(row.issues) || (text.includes("recent issue") && countsAppear(text, row.issues));
  return hasPlace && hasMiles && hasNote && hasIssues;
}

function hasIssueCounts(counts: IssueCounts | undefined): boolean {
  return Boolean(counts && (counts.delivery > 0 || counts.installation > 0 || counts.customer > 0));
}

function countsAppear(text: string, counts: IssueCounts): boolean {
  return [counts.delivery, counts.installation, counts.customer].filter((count) => count > 0).every((count) => {
    return new RegExp(`(^|\\D)${count}(\\D|$)`).test(text);
  });
}

export function suggestionFromModel(
  rows: SuggestionShortlistRow[],
  partyId: string,
  reason: string,
): CoverageSuggestion | null {
  const trimmed = reason.trim();
  if (!trimmed || !rows.some((row) => row.partyId === partyId)) return null;
  return { partyId, reason: trimmed };
}
